import { ComponentType, ReactNode, SVGProps } from 'react';
import styled from 'styled-components';

import { IconDot } from '../../common/icons/cdl/Dot';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';

const ICON_MARKER_MASK_HEIGHT = '20px';

const MarkerMask = styled(Box)`
    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        width: 16px;
        height: ${ICON_MARKER_MASK_HEIGHT};
        background: ${theme.colors.background.default};
        transform: translateY(-50%);
        z-index: -1;
    }
`;

interface TimelineItemProps {
    children: ReactNode;
    lineBefore: boolean;
    lineAfter: boolean;
    Icon?: ComponentType<SVGProps<SVGSVGElement>>;
    markerColor?: string;
}

export const TimelineItem = ({
    children,
    lineBefore,
    lineAfter,
    Icon,
    markerColor = 'foreground.subtle',
}: TimelineItemProps) => {
    const markerContent = Icon ? <Icon width={16} height={16} /> : <IconDot width={16} height={16} />;
    const resolvedMarkerColor = Icon ? 'foreground.muted' : markerColor;

    const markerProps = {
        width: '16px',
        height: '16px',
        color: resolvedMarkerColor,
        backgroundColor: 'background.default',
        position: 'relative' as const,
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    };

    const marker = <MarkerMask {...markerProps}>{markerContent}</MarkerMask>;

    const showLine = lineBefore || lineAfter;

    return (
        <Box
            display="grid"
            gridTemplateColumns="16px minmax(0, 1fr)"
            columnGap={8}
            marginLeft={16}
            alignItems="start"
            position="relative"
            paddingBottom={lineAfter ? 16 : 0}
        >
            {showLine ? (
                <Box
                    position="absolute"
                    top={lineBefore ? 0 : '16px'}
                    bottom={lineAfter ? 0 : '16px'}
                    left="8px"
                    width="1px"
                    style={{ transform: 'translateX(-50%)' }}
                    backgroundColor="border.muted"
                />
            ) : null}
            <Box display="flex" alignItems="center" justifyContent="center" width="16px">
                {marker}
            </Box>
            <Box display="flex" flexDirection="column" gap={4} minWidth={0}>
                {children}
            </Box>
        </Box>
    );
};
